import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const yesNo = (v) => (v ? "Yes" : "No");

const LIMIT = 10;

/* ── level helpers ── */
const NO_LEVEL = "none";

// Normalise a user's level into a string key ("0", "1", ... or "none")
const levelKey = (u) =>
  u?.level === null || u?.level === undefined || u?.level === ""
    ? NO_LEVEL
    : String(u.level);

const levelLabel = (key) => {
  if (key === NO_LEVEL) return "No Level";
  if (key === "0") return "Level 0 (Master)";
  return `Level ${key}`;
};

// Numeric levels ascending, non-numeric after, "No Level" last
const compareLevelKeys = (a, b) => {
  if (a === NO_LEVEL) return 1;
  if (b === NO_LEVEL) return -1;
  const na = Number(a);
  const nb = Number(b);
  const aNum = !Number.isNaN(na);
  const bNum = !Number.isNaN(nb);
  if (aNum && bNum) return na - nb;
  if (aNum) return -1;
  if (bNum) return 1;
  return a.localeCompare(b);
};

/* ── Bootstrap numbered pagination ── */
function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <nav
      aria-label="Page navigation"
      className="mt-3 d-flex justify-content-center"
    >
      <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center gap-1">
        <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page - 1)}
          >
            <i className="bi bi-chevron-left" />
          </button>
        </li>
        {getPages().map((p, i) =>
          p === "..." ? (
            <li key={`ellipsis-${i}`} className="page-item disabled">
              <span className="page-link border-0 bg-transparent">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
              <button
                className="page-link rounded-3"
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
            </li>
          ),
        )}
        <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page + 1)}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function AdminDashboard() {
  const [allUsers, setAllUsers] = useState([]); // full list from API
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  /* ── filter inputs ── */
  const [searchInput, setSearchInput] = useState(""); // name / phone / email / userId
  const [statusInput, setStatusInput] = useState("");
  const [referralInput, setReferralInput] = useState(""); // "" | "yes" | "no"
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedReferral, setAppliedReferral] = useState("");
  const [levelInput, setLevelInput] = useState(""); // "" | "0" | "1" | ... | "none"
  const [appliedLevel, setAppliedLevel] = useState("");

  /* ── pagination ── */
  const [page, setPage] = useState(1);

  const activeFilterCount = [
    appliedSearch,
    appliedStatus,
    appliedReferral,
    appliedLevel,
  ].filter(Boolean).length;

  const fetchAllUsers = async () => {
    setLoading(true);
    setErrorMsg("");

    const adminToken = localStorage.getItem("adminToken");
    if (!adminToken) {
      setErrorMsg("Admin session missing. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/Profile/GetUserProfile`, {
        method: "GET",
        headers: { Authorization: adminToken },
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Request failed (${res.status})`);
      }

      const data = await res.json();
      setAllUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, []);

  /* ── client-side filtering (everything except level) ── */
  const baseFilteredUsers = useMemo(() => {
    let list = allUsers;

    if (appliedSearch.trim()) {
      const q = appliedSearch.trim().toLowerCase();
      list = list.filter(
        (u) =>
          (u.userId || "").toLowerCase().includes(q) ||
          (u.name || "").toLowerCase().includes(q) ||
          (u.phone || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q),
      );
    }

    if (appliedStatus) {
      list = list.filter(
        (u) => (u.status || "").toLowerCase() === appliedStatus,
      );
    }

    if (appliedReferral === "yes") {
      list = list.filter((u) => u.referralActivated === true);
    } else if (appliedReferral === "no") {
      list = list.filter((u) => u.referralActivated !== true);
    }

    return list;
  }, [allUsers, appliedSearch, appliedStatus, appliedReferral]);

  /* ── all levels present in the data (for the dropdown), with total counts ── */
  const allLevelCounts = useMemo(() => {
    const map = {};
    allUsers.forEach((u) => {
      const k = levelKey(u);
      map[k] = (map[k] || 0) + 1;
    });
    return Object.keys(map)
      .sort(compareLevelKeys)
      .map((k) => ({ key: k, count: map[k] }));
  }, [allUsers]);

  /* ── per-level counts respecting the other applied filters ── */
  const levelCounts = useMemo(() => {
    const map = {};
    baseFilteredUsers.forEach((u) => {
      const k = levelKey(u);
      map[k] = (map[k] || 0) + 1;
    });
    return allLevelCounts.map(({ key }) => ({ key, count: map[key] || 0 }));
  }, [baseFilteredUsers, allLevelCounts]);

  /* ── apply level filter on top ── */
  const filteredUsers = useMemo(() => {
    if (!appliedLevel) return baseFilteredUsers;
    return baseFilteredUsers.filter((u) => levelKey(u) === appliedLevel);
  }, [baseFilteredUsers, appliedLevel]);

  // Quick-select a level from the summary chips
  const selectLevel = (key) => {
    const next = appliedLevel === key ? "" : key; // click again to toggle off
    setLevelInput(next);
    setAppliedLevel(next);
    setPage(1);
  };

  /* ── client-side pagination ── */
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / LIMIT));
  const pagedUsers = filteredUsers.slice((page - 1) * LIMIT, page * LIMIT);

  const handleApply = () => {
    setAppliedSearch(searchInput);
    setAppliedStatus(statusInput);
    setAppliedReferral(referralInput);
    setAppliedLevel(levelInput);
    setPage(1);
  };

  const handleClear = () => {
    setSearchInput("");
    setAppliedSearch("");
    setStatusInput("");
    setAppliedStatus("");
    setReferralInput("");
    setAppliedReferral("");
    setLevelInput("");
    setAppliedLevel("");
    setPage(1);
  };

  return (
    <>
      <Header />
      <main className="pb-4">
        <section className="py-4">
          <div className="container d-flex justify-content-center">
            <div className="col-12">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h3 className="m-0 fw-semibold lh-1">
                    All Users ({filteredUsers.length}
                    {activeFilterCount > 0 &&
                    allUsers.length !== filteredUsers.length
                      ? ` of ${allUsers.length}`
                      : ""}
                    )
                  </h3>
                  <div className="text-muted">All Registered Users</div>
                </div>
                <div
                  className="icon-shape icon-md bg-light-primary rounded-circle cursor-pointer"
                  onClick={fetchAllUsers}
                  title="Refresh"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-dark" />
                  ) : (
                    <i className="bi bi-arrow-clockwise fs-3 text-primary" />
                  )}
                </div>
              </div>

              {/* ── Professional Filter Card ── */}
              <div
                className="card border-0 shadow-sm rounded-4 mb-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                }}
              >
                <div className="card-body px-4 py-3">
                  <div className="row g-2 align-items-end">
                    {/* Search */}
                    <div className="col-12 col-md">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-person me-1 text-muted" />
                        Name / Phone / Email / User ID
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i
                            className="bi bi-search text-muted"
                            style={{ fontSize: 13 }}
                          />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApply()}
                          placeholder="Search users…"
                          disabled={loading}
                          style={{ fontSize: 14 }}
                        />
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-12 col-md-2">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-circle-half me-1 text-muted" />
                        Status
                      </label>
                      <select
                        className="form-select"
                        value={statusInput}
                        onChange={(e) => setStatusInput(e.target.value)}
                        disabled={loading}
                        style={{ fontSize: 14 }}
                      >
                        <option value="">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="blocked">Blocked</option>
                      </select>
                    </div>

                    {/* Referral Activated */}
                    <div className="col-12 col-md-2">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-diagram-3 me-1 text-muted" />
                        Referral Activated
                      </label>
                      <select
                        className="form-select"
                        value={referralInput}
                        onChange={(e) => setReferralInput(e.target.value)}
                        disabled={loading}
                        style={{ fontSize: 14 }}
                      >
                        <option value="">All</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>

                    {/* Level */}
                    <div className="col-12 col-md-2">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-layers me-1 text-muted" />
                        Level
                      </label>
                      <select
                        className="form-select"
                        value={levelInput}
                        onChange={(e) => setLevelInput(e.target.value)}
                        disabled={loading}
                        style={{ fontSize: 14 }}
                      >
                        <option value="">All Levels</option>
                        {allLevelCounts.map(({ key, count }) => (
                          <option key={key} value={key}>
                            {levelLabel(key)} ({count})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Buttons */}
                    <div className="col-12 col-md-auto d-flex gap-2">
                      <button
                        className="btn btn-primary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading}
                        onClick={handleApply}
                      >
                        <i className="bi bi-check2 me-1" />
                        Apply
                      </button>
                      <button
                        className="btn btn-outline-secondary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={
                          loading ||
                          (!searchInput &&
                            !appliedSearch &&
                            !statusInput &&
                            !appliedStatus &&
                            !referralInput &&
                            !appliedReferral &&
                            !levelInput &&
                            !appliedLevel)
                        }
                        onClick={handleClear}
                      >
                        <i className="bi bi-x-lg me-1" />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {errorMsg ? (
                <div className="alert alert-danger rounded-4">{errorMsg}</div>
              ) : null}

              {/* ── Users by Level summary ── */}
              {!loading && levelCounts.length > 0 ? (
                <div className="card border-0 shadow-sm rounded-4 mb-3">
                  <div className="card-body px-4 py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                        <i className="bi bi-bar-chart-steps me-1 text-primary" />
                        Users by Level
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {activeFilterCount > (appliedLevel ? 1 : 0)
                          ? "Counts reflect other applied filters"
                          : "Click a level to filter the list"}
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {levelCounts.map(({ key, count }) => {
                        const active = appliedLevel === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`btn btn-sm d-flex align-items-center gap-2 ${
                              active ? "btn-primary" : "btn-outline-primary"
                            }`}
                            style={{ borderRadius: 10, fontSize: 13 }}
                            onClick={() => selectLevel(key)}
                            title={
                              active
                                ? "Click to remove level filter"
                                : `Show users at ${levelLabel(key)}`
                            }
                          >
                            <span>{levelLabel(key)}</span>
                            <span
                              className={`badge rounded-pill ${
                                active ? "bg-white text-primary" : "bg-primary"
                              }`}
                            >
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Table */}
              <div className="card shadow-sm rounded-4">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>No.</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="2" className="text-center py-5">
                              <span className="spinner-border spinner-border-sm me-2" />
                            </td>
                          </tr>
                        ) : pagedUsers.length === 0 ? (
                          <tr>
                            <td
                              colSpan="2"
                              className="text-center py-5 text-muted"
                            >
                              {activeFilterCount > 0
                                ? "No users match your filters."
                                : "No users found."}
                            </td>
                          </tr>
                        ) : (
                          pagedUsers.map((u, idx) => (
                            <tr key={u?._id || idx}>
                              <td className="fw-semibold">
                                {(page - 1) * LIMIT + idx + 1}.
                              </td>
                              <td>
                                <div className="lh-sm my-2">
                                  <div>
                                    <span className="text-muted">User ID</span>{" "}
                                    —{" "}
                                    <span className="fw-semibold">
                                      {u.userId || "-"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">Name</span> —{" "}
                                    {u.name || "-"}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Mobile No.
                                    </span>{" "}
                                    — {u.phone || "-"}
                                  </div>
                                  <div>
                                    <span className="text-muted">Email ID</span>{" "}
                                    — {u.email || "-"}
                                  </div>
                                  <div>
                                    <span className="text-muted">Status</span> —{" "}
                                    <span
                                      className={`badge text-capitalize ${u.status === "active" ? "bg-success" : "bg-secondary"}`}
                                    >
                                      {u.status || "-"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">Level</span> —{" "}
                                    <span className="badge bg-info text-dark">
                                      {levelKey(u) === NO_LEVEL
                                        ? "-"
                                        : levelLabel(levelKey(u))}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Parent ID
                                    </span>{" "}
                                    — {u.parentId || "-"}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Referral Code
                                    </span>{" "}
                                    — {u.referralCode || "-"}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Referred By
                                    </span>{" "}
                                    — {u.referredBy || "-"}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Phone Verified
                                    </span>{" "}
                                    — {yesNo(u.isPhoneVerified)}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Email Verified
                                    </span>{" "}
                                    — {yesNo(u.isEmailVerified)}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      First Payment Done
                                    </span>{" "}
                                    — {yesNo(u.firstPaymentDone)}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Referral Activated
                                    </span>{" "}
                                    — {yesNo(u.referralActivated)}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Wallet Balance
                                    </span>{" "}
                                    — ₹{u.walletBalance ?? 0}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Total Earnings
                                    </span>{" "}
                                    — ₹{u.totalEarnings ?? 0}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Created At
                                    </span>{" "}
                                    — {formatDate(u.createdAt)}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Updated At
                                    </span>{" "}
                                    — {formatDate(u.updatedAt)}
                                  </div>
                                  <hr className="w-25" />
                                  <div>
                                    <span className="text-muted">
                                      Name as per Bank
                                    </span>{" "}
                                    — {u?.nameOnBank}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Bank Account No.
                                    </span>{" "}
                                    — {u?.bankAccountNo}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      IFSC Code
                                    </span>{" "}
                                    — {u?.ifsc}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      UPI ID
                                    </span>{" "}
                                    — {u?.upiId}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
                <div className="text-muted small">
                  Showing {pagedUsers.length ? (page - 1) * LIMIT + 1 : 0}–
                  {Math.min(page * LIMIT, filteredUsers.length)} of{" "}
                  {filteredUsers.length} users
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AdminDashboard;